'use client'

import { useEffect, useState } from 'react';
import { XIcon, CheckIcon } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import QuestionnaireForm from '@/modules/management-mandate/questionnaire/form';
import { questionList } from '@/core/utils/management-mandate';
import { useManagementMandateStore } from '@/store/management-mandate';
import { useModalStore } from '@/store/modal/store';
import { MandateService } from '@/core/services/mandateService';
import Loading from '@/core/components/forms/fields/loading';
import InvestorProfile from './inverstor-profile';

enum CredentialStatus {
  TODO = "todo",
  IN_PROCESS = "in-process",
  VERIFIED = "verified"
}

const statusDetector = (index: number, current: number) => {
  if (index === current) {
    return CredentialStatus.IN_PROCESS;
  }
  if (index < current) {
    return CredentialStatus.VERIFIED;
  }
  return CredentialStatus.TODO;
}

const MAX_STEP = questionList.length - 1;

const getCurrentStep = (questionnaire: any) => {
  const index = questionList.findIndex((item: any) => !item.questions.every((pk: any) => get(questionnaire, pk.name, '')));
  return index >= 0 ? index : Object.keys(questionnaire).length ? MAX_STEP : 0;
}

interface MandateQuestionnaireWrapperProps {
  onClose: () => void;
}

const MandateQuestionnaireWrapper = ({ onClose }: MandateQuestionnaireWrapperProps) => {
  const { t } = useTranslation();
  const questionnaire = useManagementMandateStore((state) => state.questionnaire);
  const setQuestionnaire = useManagementMandateStore((state) => state.setQuestionnaire);
  const setUserManagementProfile = useManagementMandateStore((state) => state.setUserManagementProfile);
  const setModal = useModalStore((state) => state.setModal);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isStart, setIsStart] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [showInvestorProfile, setShowInvestorProfile] = useState<boolean>(false);

  const { mutate: getInvestInfoUserCurrent, isLoading: isLoadingGetInvestInfoUserCurrent } = useMutation({
    mutationFn: MandateService.getInvestInfoUserCurrent,
    onSuccess: (data) => {
      if (data.answers) {
        setQuestionnaire(JSON.parse(data.answers));
        setCurrentStep(getCurrentStep(JSON.parse(data.answers)));
      } else {
        setCurrentStep(getCurrentStep(questionnaire));
      }
      setUserManagementProfile(data);
      setIsInitialized(true);

      // If user already has investor profile, show it directly
      if (data.investorProfile) {
        setShowInvestorProfile(true);
      }
    },
    onError: () => {
      setIsInitialized(true);
    }
  });

  const { mutate: updateInvestInfoUser, isLoading } = useMutation({
    mutationFn: () => MandateService.updateInvestInfoUser({ answers: questionnaire, currentStep: "mandate", totalScore: 0 }),
    onSuccess: () => {
      // Questionnaire completed, show investor profile
      setShowInvestorProfile(true);
    },
    onError: (error) => {
      console.error('Failed to save questionnaire:', error);
    }
  });

  const onNext = () => {
    if (currentStep === MAX_STEP) {
      updateInvestInfoUser();
    } else {
      setCurrentStep(Math.min(currentStep + 1, MAX_STEP));
      setIsStart(true);
    }
  };

  const onBack = () => {
    setIsStart(false);
    if (currentStep === 0) {
      onClose();
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  const handleReset = () => {
    setShowInvestorProfile(false);
    setCurrentStep(0);
    setQuestionnaire({});
    getInvestInfoUserCurrent();
  };

  const handleCloseInvestorProfile = () => {
    onClose();
    // Open profile select modal if needed
    setModal({ managementProfileSelect: true });
  };

  useEffect(() => {
    getInvestInfoUserCurrent();
  }, []);

  if (isLoadingGetInvestInfoUserCurrent || !isInitialized) {
    return (
      <div className="general-modal dca-questionnaire-modal">
        <div className="general-modal__header">
          {t('management_mandate.investor_profile_on_digital_assets')}
          <div className="general-modal__close" onClick={onClose}>
            <XIcon />
          </div>
        </div>
        <div className="general-modal__content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <Loading />
        </div>
      </div>
    );
  }

  // Show investor profile after questionnaire completion
  if (showInvestorProfile) {
    return (
      <InvestorProfile 
        reset={handleReset} 
        onClose={handleCloseInvestorProfile} 
      />
    );
  }

  return (
    <div className="general-modal dca-questionnaire-modal">
      <div className="general-modal__header">
        {t('management_mandate.investor_profile_on_digital_assets')}
        <div className="general-modal__close" onClick={onClose}>
          <XIcon />
        </div>
      </div>
      <div className="general-modal__content">
        <div className="dca-questionnaire-wrapper">
          {/* Sidebar with steps */}
          <div className="dca-questionnaire-sidebar">
            {questionList.map((e, index) => {
              const status = statusDetector(index, currentStep);
              const isVerified = status === CredentialStatus.VERIFIED;

              return (
                <div
                  key={`menu-${e.title}-${index}-${currentStep}`}
                  className={`dca-questionnaire-sidebar__item ${currentStep === index ? 'active' : ''}`}
                >
                  <div className={`badge ${status}`}>
                    <progress></progress>
                    {isVerified && <CheckIcon size={20} />}
                  </div>
                  <div className="title">
                    <h3>{t(e.title)}</h3>
                    {isVerified && <p className='sub-text'>{t('common.verified')}</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Form content */}
          <div className="dca-questionnaire-content">
            <QuestionnaireForm
              key={currentStep}
              onNext={onNext}
              onBack={onBack}
              dataQuestion={questionList[currentStep]}
              isStart={isStart}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MandateQuestionnaireWrapper;
